// =============================================================================
// サバイバル状態（体力・空腹・喉の渇き・スタミナ）。純粋なロジック。
// =============================================================================
import type { Stats, FoodEffect } from './types';

export const stats: Stats = {
  health: 100, maxHealth: 100,
  hunger: 100, maxHunger: 100,   // 0で餓死ダメージ
  thirst: 100, maxThirst: 100,   // 0で脱水ダメージ
  stamina: 100, maxStamina: 100, // 走り/泳ぎ加速で消費
  oxygen: 100, maxOxygen: 100,   // 潜水で減り、0で溺れダメージ。水面で急速回復
  alive: true,
};

// 毎秒の自然減少（空腹15分/渇き10分で空に。渇きの方が切迫する）
const HUNGER_RATE = 100 / 900;
const THIRST_RATE = 100 / 600;
const STARVE_DMG = 2.0;     // 空腹or脱水が0になると体力が毎秒これだけ減る（約25秒で危険域）
const REGEN = 0.8;          // 空腹>40 かつ 渇き>40 のとき体力回復/秒
const STAMINA_DRAIN = 26;   // 走り/速泳ぎ時/秒
const STAMINA_REGEN = 16;   // 平常時/秒
const STAMINA_RECOVER = 30; // 枯渇後にスプリント再開できる回復量(境界5での毎フレーム振動を防ぐヒステリシス)
let exhausted = false;      // 枯渇状態(Shift押しっぱなしでも一度歩かせて回復の弧を作る)
const O2_DRAIN = 100 / 50;  // 潜水で約50秒もつ(深い財宝への往復がぎりぎり=緊張)
const O2_REGEN = 100 / 4;   // 水面で約4秒で全回復
const DROWN_DMG = 7;        // 酸素0の溺れダメージ/秒(すぐ浮上すれば助かる)

// 新規ゲーム用に初期化
export function resetSurvival(): void {
  stats.health = stats.maxHealth;
  stats.hunger = stats.maxHunger;
  stats.thirst = stats.maxThirst;
  stats.stamina = stats.maxStamina;
  stats.oxygen = stats.maxOxygen;
  stats.alive = true;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function tickSurvival(dt: number, opts: { sprinting?: boolean; movingHard?: boolean; drainMul?: number; diving?: boolean; oxygenMul?: number } = {}): void {
  if (!stats.alive) return;
  const sprinting = !!opts.sprinting;
  const movingHard = !!opts.movingHard;
  const mul = opts.drainMul ?? 1; // 夜間の冷え等で消耗が速くなる

  stats.hunger = clamp(stats.hunger - HUNGER_RATE * mul * dt, 0, stats.maxHunger);
  stats.thirst = clamp(stats.thirst - THIRST_RATE * mul * dt, 0, stats.maxThirst);

  if (sprinting && movingHard) stats.stamina = clamp(stats.stamina - STAMINA_DRAIN * dt, 0, stats.maxStamina);
  else stats.stamina = clamp(stats.stamina + STAMINA_REGEN * dt, 0, stats.maxStamina);
  if (stats.stamina <= 5) exhausted = true;
  else if (stats.stamina >= STAMINA_RECOVER) exhausted = false; // 新規/リセット時もstamina=100でここを通り解除される

  // 酸素: 潜水中に減り(oxygenMul=真珠の御守で緩やかに)、それ以外(水面/陸)で急速回復
  if (opts.diving) stats.oxygen = clamp(stats.oxygen - O2_DRAIN * (opts.oxygenMul ?? 1) * dt, 0, stats.maxOxygen);
  else stats.oxygen = clamp(stats.oxygen + O2_REGEN * dt, 0, stats.maxOxygen);

  let dmg = 0;
  if (stats.hunger <= 0) dmg += STARVE_DMG;
  if (stats.thirst <= 0) dmg += STARVE_DMG;
  if (opts.diving && stats.oxygen <= 0) dmg += DROWN_DMG; // 溺れ
  if (dmg > 0) {
    stats.health = clamp(stats.health - dmg * dt, 0, stats.maxHealth);
  } else if (stats.hunger > 40 && stats.thirst > 40) {
    stats.health = clamp(stats.health + REGEN * dt, 0, stats.maxHealth);
  }
  if (stats.health <= 0) { stats.health = 0; stats.alive = false; }
}

export function canSprint(): boolean { return !exhausted && stats.stamina > 5; }
export function isExhausted(): boolean { return exhausted; } // HUD用(スタミナバーの明滅)

export function eat(item: FoodEffect): void {
  if (item.hunger) stats.hunger = clamp(stats.hunger + item.hunger, 0, stats.maxHunger);
  if (item.thirst) stats.thirst = clamp(stats.thirst + item.thirst, 0, stats.maxThirst);
  if (item.health) stats.health = clamp(stats.health + item.health, 0, stats.maxHealth);
}

export function damage(amount: number): void {
  stats.health = clamp(stats.health - amount, 0, stats.maxHealth);
  if (stats.health <= 0) stats.alive = false;
}
